import { useEffect, useState } from "react";
import CreatableSelect from "react-select";
import makeAnimated from "react-select/animated";
import {
  createNewComission,
  updateComission,
} from "@store/internal-study/comissions-store";
import {
  selectColourStylesSigning,
  selectTheme,
} from "@components/common/common";
import { $Employees, fetchEmployees } from "@store/company/employees-store";
import { ComissionMember } from "@interfaces/store/comissions-store-types";
import { IconButton, Grid2 as Grid, TextField } from "@mui/material";
import moment, { Moment } from "moment";
import { DatePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { ruRU } from "@mui/x-date-pickers/locales";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import useModal from "@modals/modal-hook";
import useStyles from "@ui/material-ui-styles";
import { useUnit } from "effector-react";
import { useSelectedCompanyQuery } from "@lib/company";
import style from "@scss/modals/company/create-comission.module.scss";
import clsx from "clsx";

const animatedComponents = makeAnimated();

const CreateComissionModal = () => {
  const { modalComponent, modalData, close } = useModal();
  const classes = useStyles();
  const employees = useUnit($Employees);
  const [employeesOptions, setEmployeeOptions] =
    useState<{ label: string; value: string; position: string }[]>();
  const [orderDate, setOrderDate] = useState<Moment>(
    modalData.order_date ? moment(modalData.order_date) : moment()
  );
  const [chairman, setChairman] = useState<{
    label: string;
    value: string;
    position: string;
  }>(modalData.chairman ?? null);
  const [members, setMembers] = useState<
    ({ label: string; value: string; position: string } | null)[]
  >(modalData.members ?? [null]);
  const { company } = useSelectedCompanyQuery();

  useEffect(() => {
    fetchEmployees();
  }, []);

  useEffect(() => {
    setEmployeeOptions(
      employees.map((employee) => ({
        label: employee.name,
        value: employee.email,
        position: employee.position.title,
      }))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employees.length]);

  const changeChairman = (value: any) => {
    setChairman(value);
  };

  const addMembers = (e: any) => {
    members.push(null);
    return setMembers([...members]);
  };

  const removeMembers = (index: number) => {
    if (members.length > 1) members.splice(index, 1);
    return setMembers([...members]);
  };

  const changeMembers = (value: any, index: number) => {
    members[index] = value;
    return setMembers([...members]);
  };

  const handleChangeOrderDate = (date: any) => {
    setOrderDate(date ?? null);
  };

  const handleUpdateOrderDate = () => {
    if (!orderDate?.isValid() || !orderDate) {
      return setOrderDate(orderDate);
    }
  };

  const handleSubmit = (e: any) => {
    e.preventDefault();
    if (!company) return;

    const modalBtn = document.querySelector(
      "button.modal_btn"
    ) as HTMLButtonElement;
    modalBtn.disabled = true;

    const titleField = document.querySelector(
      'input[name="title"]'
    ) as HTMLInputElement;

    const orderField = document.querySelector(
      'input[name="order_number"]'
    ) as HTMLInputElement;

    if (modalData.editMode) {
      updateComission({
        comissionId: modalData.comissionId,
        title: titleField.value,
        order_number: orderField.value,
        order_date: orderDate?.toISOString(),
        chairman:
          chairman !== undefined
            ? chairman
            : {
                label: "Ген. директор",
                value: "",
                position: "Генеральный директор",
              },
        members: members as ComissionMember[],
        cb: (err) => {
          if (err) {
            return console.error("При изменении комиссии произошла ошибка");
          }
          close();
        },
      });
    } else {
      createNewComission({
        companyId: company.id,
        title: titleField.value,
        order_number: orderField.value,
        order_date: orderDate?.toISOString(),
        chairman:
          chairman !== undefined
            ? chairman
            : {
                label: "Ген. директор",
                value: "",
                position: "Генеральный директор",
              },
        members: members as ComissionMember[],
        cb: (err) => {
          if (err) {
            return console.error("При создании подразделения произошла ошибка");
          }
          close();
        },
      });
    }
  };

  return (
    <div
      key={modalComponent.key}
      className={clsx(style.create_comission_modal)}
    >
      <p className="modal_title">{modalData.modalTitle}</p>
      <div className="underline" />
      <form className={`modal_content ${classes.root}`} onSubmit={handleSubmit}>
        <TextField
          name="title"
          label="Наименование комиссии"
          variant="filled"
          required
          defaultValue={modalData.title || ""}
        />
        <Grid container spacing={3}>
          <Grid size={{ xs: 6, md: 6 }}>
            <TextField
              name="order_number"
              label="№ приказа"
              variant="filled"
              required
              fullWidth
              defaultValue={modalData.order_number || ""}
            />
          </Grid>
          <Grid size={{ xs: 6, md: 6 }}>
            <LocalizationProvider
              dateAdapter={AdapterMoment}
              adapterLocale={"ru"}
              localeText={
                ruRU.components.MuiLocalizationProvider.defaultProps.localeText
              }
            >
              <DatePicker
                value={orderDate}
                onChange={handleChangeOrderDate}
                onClose={handleUpdateOrderDate}
                sx={{ width: "100%" }}
                format="DD.MM.YYYY"
                autoFocus={false}
                slotProps={{
                  actionBar: { actions: ["cancel", "accept"] },
                  textField: {
                    variant: "filled",
                  },
                }}
                label="Дата приказа"
                localeText={{
                  toolbarTitle: "",
                  cancelButtonLabel: "Отменить",
                  okButtonLabel: "ОК",
                }}
              />
            </LocalizationProvider>
          </Grid>
        </Grid>
        <p className={clsx(style.title_member)}>Председатель комиссии:</p>
        <CreatableSelect
          components={animatedComponents}
          value={chairman}
          options={employeesOptions}
          isSearchable
          placeholder={"Сотрудники"}
          onChange={(value: any) => changeChairman(value as any)}
          styles={selectColourStylesSigning()}
          theme={selectTheme}
          menuPlacement="bottom"
          menuPortalTarget={document.body}
          maxMenuHeight={200}
          noOptionsMessage={() => "Ничего не найдено"}
        />
        <p className={clsx(style.title_member)}>
          Члены комиссии:
          <IconButton
            aria-label="fingerprint"
            color="success"
            onClick={addMembers}
          >
            <AddCircleOutlineIcon />
          </IconButton>
        </p>
        {members &&
          members.map((e, i) => (
            <div className={clsx(style.member_remove)}>
              <CreatableSelect
                components={animatedComponents}
                value={e}
                options={employeesOptions}
                isSearchable
                placeholder={"Сотрудники"}
                onChange={(value: any) => changeMembers(value as any, i)}
                styles={selectColourStylesSigning({
                  width: "100%",
                  marginTop: "0px",
                })}
                theme={selectTheme}
                menuPlacement="bottom"
                menuPortalTarget={document.body}
                maxMenuHeight={200}
                noOptionsMessage={() => "Ничего не найдено"}
              />
              <IconButton
                aria-label="fingerprint"
                color="success"
                onClick={() => removeMembers(i)}
              >
                <RemoveCircleOutlineIcon />
              </IconButton>
            </div>
          ))}

        <button type="submit" className="modal_btn">
          {modalComponent.btnText}
        </button>
      </form>
    </div>
  );
};

export default CreateComissionModal;
